import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Divider, Drawer, Flex, Segmented, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  CHANNEL_SORT_MODES,
  ChannelCategoryEntity,
  ChannelFilters,
  ChannelSortMode,
  ChannelType,
  NO_CATEGORY_SLUG,
  channelSortMode,
  withChannelSortMode,
} from '../../../domain';

export interface ChannelFiltersSheetProps {
  open: boolean;
  isMobile: boolean;
  filters: ChannelFilters;
  categories: ChannelCategoryEntity[];
  totalCount: number;
  onClose: () => void;
  onApply: (filters: ChannelFilters) => void;
  onPreviewCount?: (filters: ChannelFilters) => Promise<number>;
}

const PREVIEW_DEBOUNCE_MS = 300;

type TypeSegment = 'all' | ChannelType.FREE | ChannelType.PAID;

const ChannelFiltersSheet: React.FC<ChannelFiltersSheetProps> = ({
  open, isMobile, filters, categories, totalCount, onClose, onApply, onPreviewCount,
}) => {
  const { t } = useTranslation();
  // Черновик: фильтры применяются только по кнопке, иначе на мобиле список
  // перезагружался бы под пальцем на каждый тап.
  const [draft, setDraft] = useState<ChannelFilters>(filters);
  const [previewCount, setPreviewCount] = useState<number | null>(null);

  const previewCountRef = useRef(onPreviewCount);
  previewCountRef.current = onPreviewCount;

  useEffect(() => { if (open) setDraft(filters); }, [open, filters]);

  const draftKey = JSON.stringify(draft);

  useEffect(() => {
    const requestPreview = previewCountRef.current;
    if (!open || !requestPreview) {
      setPreviewCount(null);
      return;
    }

    let cancelled = false;
    setPreviewCount(null);

    const timer = setTimeout(() => {
      requestPreview(JSON.parse(draftKey) as ChannelFilters)
        .then((count) => { if (!cancelled) setPreviewCount(count); })
        .catch(() => { if (!cancelled) setPreviewCount(null); });
    }, PREVIEW_DEBOUNCE_MS);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, draftKey]);

  const selectedTypeSegment: TypeSegment = (draft.types?.length === 1 ? draft.types[0] : 'all') as TypeSegment;

  const handleTypeChange = (value: TypeSegment) => {
    setDraft({ ...draft, types: value === 'all' ? [] : [value] });
  };

  const handleSortModeChange = (value: ChannelSortMode) => {
    setDraft(withChannelSortMode(draft, value));
  };

  const handleReset = () => setDraft({ ...draft, categories: [], types: [] });

  const handleApply = () => { onApply(draft); onClose(); };

  const categoryOptions = [
    ...categories.map((category) => ({
      label: `${category.name}${category.channelsCount !== undefined ? ` (${category.channelsCount})` : ''}`,
      value: category.slug,
    })),
    { label: t('channel-no-category', { ns: 'labels' }), value: NO_CATEGORY_SLUG },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement={isMobile ? 'bottom' : 'right'}
      height={isMobile ? '80%' : undefined}
      title={t('channel-filters', { ns: 'labels' })}
      styles={{ body: { paddingTop: 8 } }}
      footer={
        <div className="channel-picker__filters-footer">
          <Button onClick={handleReset}>{t('reset-filters', { ns: 'buttons' })}</Button>
          <Button type="primary" onClick={handleApply}>
            {previewCount === null
              ? t('apply-filters', { ns: 'buttons' })
              : t('show-n-channels', { ns: 'labels', count: previewCount })}
          </Button>
        </div>
      }
    >
      <Flex vertical gap="small">
        <Typography.Text strong>{t('channel-filters-categories', { ns: 'labels' })}</Typography.Text>
        <Checkbox.Group
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          options={categoryOptions}
          value={draft.categories ?? []}
          onChange={(values) => setDraft({ ...draft, categories: values as string[] })}
        />

        <Divider style={{ margin: '12px 0' }} />

        <Typography.Text strong>{t('channel-filters-type', { ns: 'labels' })}</Typography.Text>
        <Segmented
          block
          value={selectedTypeSegment}
          onChange={(value) => handleTypeChange(value as TypeSegment)}
          options={[
            { label: t('channel-type-all', { ns: 'labels' }), value: 'all' },
            { label: t('channel-type-free', { ns: 'labels' }), value: ChannelType.FREE },
            { label: t('channel-type-paid', { ns: 'labels' }), value: ChannelType.PAID },
          ]}
        />

        <Divider style={{ margin: '12px 0' }} />

        <Typography.Text strong>{t('channel-filters-sort', { ns: 'labels' })}</Typography.Text>
        <Segmented
          block
          value={channelSortMode(draft)}
          onChange={(value) => handleSortModeChange(value as ChannelSortMode)}
          options={CHANNEL_SORT_MODES.map((mode) => ({ label: t(`channel-sort-${mode}`, { ns: 'labels' }), value: mode }))}
        />
      </Flex>
    </Drawer>
  );
};

export default ChannelFiltersSheet;
